import React from "react";
import {
  FaLaptop,
  FaLock,
  FaTrophy,
  FaEye,
  FaBolt,
  FaCode,
  FaSitemap,
  FaPenNib,
} from "react-icons/fa";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

const SecuritySection = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= SEO TOOL FEATURES ================= */
  const services = [
    { icon: <FaLaptop />, label: "SEO Site Audit" },
    { icon: <FaTrophy />, label: "Ranking Optimization" },
    { icon: <FaLock />, label: "Safe One-Click Fixes" },
    { icon: <FaEye />, label: "Actionable SEO Insights" },
    { icon: <FaBolt />, label: "Faster Search Performance" },
    { icon: <FaCode />, label: "Developer Friendly Setup" },
    { icon: <FaSitemap />, label: "Structured SEO Management" },
    { icon: <FaPenNib />, label: "Content Optimization" },
  ];

  /* ================= THEME ================= */
  const sectionBg = isDarkMode ? "bg-gray-950" : "bg-[#F9FBFF]";
  const textMain = isDarkMode ? "text-white" : "text-black";
  const textPara = isDarkMode ? "text-gray-400" : "text-gray-600";

  const cardBg = isDarkMode
    ? "bg-gray-900 border-gray-800 text-white"
    : "bg-white border-gray-200 text-gray-800";

  const circleColor = isDarkMode ? "bg-[#008300]/15" : "bg-[#008300]/10";

  const accentLine = isDarkMode ? "bg-[#008300]/80" : "bg-[#008300]";

  return (
    <section
      aria-label="AI powered SEO automation and website optimization features"
      className={`relative py-24 px-6 md:px-16 overflow-hidden ${sectionBg}`}
    >

      {/* ================= STRUCTURED DATA ================= */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation platform that improves website rankings, performs automated SEO audits, and optimizes website content.",
            url: "https://novarsis.ai/",
          }),
        }}
      />

      {/* ================= HIDDEN SEO TEXT ================= */}
      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation platform designed to improve
        website rankings, automate WordPress SEO optimization, analyze SEO
        metrics, and increase organic traffic using smart SEO technology.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}
      <p className="sr-only">
        AI SEO tool, automated SEO audit software, website ranking optimization,
        WordPress SEO automation platform, SEO keyword optimization tool,
        technical SEO analysis platform, AI SEO analytics dashboard.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}
      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= DECORATIVE BACKGROUND ================= */}
      <div
        aria-hidden="true"
        className={`absolute -top-24 -left-24 w-72 h-72 ${circleColor} rounded-full`}
      />

      <div
        aria-hidden="true"
        className={`absolute bottom-0 right-0 w-80 h-80 ${circleColor} rounded-full`}
      />

      {/* Left Accent Border */}
      <div
        aria-hidden="true"
        className={`absolute left-0 top-0 h-full w-1 ${accentLine}`}
      />

      <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 items-center gap-14">

        {/* ================= LEFT IMAGE ================= */}
        <figure className="relative flex justify-center group">

          {/* Glow */}
          <div
            aria-hidden="true"
            className={`absolute -inset-6 ${circleColor} blur-3xl rounded-3xl opacity-70`}
          />

          <div
            className="relative w-80 h-80 md:w-96 md:h-96 rounded-3xl overflow-hidden shadow-2xl
            transform transition duration-700 group-hover:scale-105 group-hover:rotate-1"
          >
            <img
              src="/Images/SeoGrowth.png"
              alt="Seo Up Rank AI SEO automation dashboard improving website rankings"
              title="Seo Up Rank AI SEO optimization dashboard"
              width="384"
              height="384"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          </div>

          <figcaption className="sr-only">
            Seo Up Rank AI SEO automation dashboard showing SEO performance growth.
          </figcaption>

        </figure>

        {/* ================= RIGHT CONTENT ================= */}
        <article className="relative">

          {/* Side Line */}
          <div
            aria-hidden="true"
            className={`absolute -left-6 top-0 bottom-0 w-1 rounded-full hidden md:block ${accentLine}`}
          />

          <header>

            <Reveal>
              <p className="text-[#008300] font-semibold mb-2 uppercase tracking-widest text-sm">
                Advanced SEO Automation
              </p>
            </Reveal>

            <Reveal>
              <h2
                className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-5 leading-tight ${textMain}`}
              >
                Powering Your Website
                <br />
                <span className="text-[#008300]">
                  SEO Growth Automatically
                </span>
              </h2>
            </Reveal>

            {/* Additional hidden SEO heading */}
            <h2 className="sr-only">
              AI powered SEO automation software for website optimization
            </h2>

          </header>

          <Reveal>
            <p
              className={`mb-10 text-justify leading-relaxed max-w-xl ${textPara}`}
            >
              <strong>Seo Up Rank</strong> automates technical SEO, content
              optimization, and website structure improvements to help
              businesses achieve higher search rankings, improved visibility,
              and faster organic traffic growth using AI-powered SEO technology.
            </p>
          </Reveal>

          {/* ================= SERVICES GRID ================= */}
          <div
            role="list"
            aria-label="Seo Up Rank SEO automation features"
            className="grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            {services.map((service, index) => (
              <article
                key={index}
                role="listitem"
                className={`group flex sm:flex-col lg:flex-row items-center justify-center rounded-xl px-4 py-4 shadow-md
                transition-all duration-500 cursor-pointer
                hover:border-[#008300] hover:shadow-xl
                border ${cardBg}`}
              >

                <Reveal>
                  <div
                    aria-hidden="true"
                    className="p-3 rounded-lg text-lg mr-3 transition
                    bg-[#008300]/10 text-[#008300]
                    group-hover:bg-[#008300] group-hover:text-white"
                  >
                    {service.icon}
                  </div>
                </Reveal>

                <Reveal>
                  <h3 className="font-semibold text-center text-xs sm:text-sm md:text-base">
                    {service.label}
                  </h3>
                </Reveal>

              </article>
            ))}
          </div>

          {/* ================= INTERNAL LINK ================= */}
          <div className="sr-only">
            <nav className="mt-10">
              <a
                href="/pricing"
                className="text-[#008300] font-semibold hover:underline"
                aria-label="View Seo Up Rank pricing plans"
              >
                Explore Seo Up Rank Pricing →
              </a>
            </nav>
          </div>

        </article>

      </div>
    </section>
  );
};

export default SecuritySection;